/**
 * Sosyal biliş: yanlış inanış ve sosyal alt metin.
 *
 * Zihin kuramı — bir başkasının kafasındaki resmin kendi kafamızdakine
 * benzemeyebileceğini anlamak. Klinikte otizm spektrumunda ve sosyal
 * iletişim güçlüğünde en çok zorlanılan yer burası ve piyasadaki
 * programların çoğunda bu modül hiç yok.
 *
 * SENARYOLAR DİL MODELİNDEN DEĞİL ŞABLONDAN. İstek listesinde "yapay
 * zeka ile sonsuz senaryo" yazıyordu; aynı tohum aynı sayfayı vermek
 * zorunda ve puanlama determinist olmak zorunda. Buradaki kurgular
 * yuvalardan üretiliyor: ad, nesne, iki kap, ipucu. Sayı sonsuz değil
 * ama binlerce ve hepsi denetlenebilir.
 *
 * KONTROL SORULARI ŞABLONUN PARÇASI, süs değil. Yanlış inanış
 * sorusunu bilemeyen bir çocuk iki ayrı sebeple bilemiyor olabilir:
 * ya başkasının bakış açısını kuramıyor, ya da hikâyeyi hatırlamıyor.
 * İkisi iki ayrı bulgu ve iki ayrı yönlendirme demek. Gerçeklik ve
 * bellek soruları o ayrımı yapıyor — klasik protokolde de böyle.
 */

import { Rng } from '../../prng'
import { basHarf, belirtme, foldTr, tamlayan, yonelme } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult, RenderNode } from '../types'
import type { GozCizimi } from '../../render/gozler'

/**
 * Kap havuzu: ünsüz yumuşaması OLMAYAN sözcükler.
 *
 * "sandık" → "sandığa", "dolap" → "dolaba" ve bu düzensizlik üç ek
 * kuralıyla çıkmıyor. Havuzu seçerken çözmek, her cümlede sözlük
 * tutmaktan ucuz.
 */
const KAPLAR = ['kutu', 'sepet', 'çanta', 'masa', 'raf', 'çekmece', 'torba', 'valiz']
/**
 * Nesne havuzu: belirtme eki DÜZENLİ olan sözcükler.
 *
 * "tarak" → "tarağı", "bardak" → "bardağı" (ünsüz yumuşaması) ve
 * "saat" → "saati" (ince okunan alıntı; kalın ünlüsüne rağmen ince ek
 * alıyor). Üçü de sözlükten gelen düzensizlik. İstek listesindeki
 * örnek cümle "saat" ile kuruluydu ve şablona girseydi sayfa
 * "saatı" yazardı.
 */
const NESNELER = ['top', 'kalem', 'bilye', 'anahtar', 'defter', 'makas', 'cetvel', 'silgi']
const ADLAR = ['Ali', 'Ayşe', 'Mert', 'Zeynep', 'Can', 'Elif', 'Deniz', 'Kaan', 'Buse', 'Ömer']

/* ================================================================== *
 * 36. Yanlış inanış
 *
 * Klasik beklenmedik yer değişimi kurgusu: biri nesneyi bir yere
 * koyup çıkıyor, başkası taşıyor, ilki geri dönüyor.
 *
 * YÜKSEK DÜZEYDE İKİNCİ DERECE. Birinci derecede soru "Ali nerede
 * arayacak" — Ali taşımayı görmedi, eski yerde arar. İkinci derecede
 * Ali pencereden gizlice görüyor ama Ayşe bunu bilmiyor: "Ali nerede
 * arayacak" cevabı YENİ yer olurken, "Ayşe, Ali'nin nerede arayacağını
 * düşünüyor" cevabı ESKİ yer. İki soru aynı hikâyede iki ayrı zihnin
 * resmini istiyor.
 *
 * Cevaplar iki kaptan biri: paletten dokunarak veriliyor, çünkü kap
 * adını yazmak ölçtüğümüz şeye yazı yükü ekliyor.
 * ================================================================== */
export const falseBelief: ExerciseTemplate = {
  code: 'false_belief',
  title: 'Nerede arayacak?',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** İkinci derece yanlış inanış 7. düzeyden sonra */
      ikinciDerece: l >= 7,
      /** Paletteki kap sayısı: fazlası tahmin etmeyi zorlaştırıyor */
      secenek: scale(l, 2, 4),
      /** Kaç hikâye */
      hikaye: l >= 4 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const ikinci = p['ikinciDerece'] as boolean
    const secenekSayisi = p['secenek'] as number
    const hikayeSayisi = p['hikaye'] as number

    const render: RenderNode[] = []
    const cozumler: { id: string; beklenen: string; tur: string }[] = []

    for (let h = 0; h < hikayeSayisi; h++) {
      const adlar = rng.shuffle(ADLAR)
      const [a, b] = [adlar[0]!, adlar[1]!]
      const nesne = rng.pick(NESNELER)
      const kaplar = rng.shuffle(KAPLAR)
      const eski = kaplar[0]!
      const yeni = kaplar[1]!
      // Çeldirici kaplar: hikâyede hiç geçmeyenler
      const celdirici = kaplar.slice(2, 2 + Math.max(0, secenekSayisi - 2))

      const nesneBel = belirtme(nesne)
      const metin = [
        `${a}, ${nesneBel} ${yonelme(eski)} koydu.`,
        `Sonra ${a} odadan çıktı.`,
        ikinci
          ? `${a} yokken ${b}, ${nesneBel} ${yonelme(yeni)} taşıdı. ${a} bunu pencereden gizlice gördü ama ${b} görüldüğünü bilmiyor.`
          : `${a} yokken ${b}, ${nesneBel} ${yonelme(yeni)} taşıdı.`,
        `${a} geri geldi.`,
      ].join(' ')

      render.push({ t: 'text', value: `${h + 1}. ${metin}`, size: 'md' })

      const sorular: { soru: string; cevap: string; tur: string }[] = ikinci
        ? [
            { soru: `${a} ${nesneBel} nerede arayacak?`, cevap: yeni, tur: 'inanis' },
            {
              soru: `${b}, ${tamlayan(a, true)} nerede arayacağını düşünüyor?`,
              cevap: eski,
              tur: 'ikinci',
            },
            { soru: `${basHarf(nesne)} şimdi nerede?`, cevap: yeni, tur: 'gerceklik' },
            { soru: `${basHarf(nesne)} başta neredeydi?`, cevap: eski, tur: 'bellek' },
          ]
        : [
            { soru: `${a} ${nesneBel} nerede arayacak?`, cevap: eski, tur: 'inanis' },
            { soru: `${basHarf(nesne)} şimdi nerede?`, cevap: yeni, tur: 'gerceklik' },
            { soru: `${basHarf(nesne)} başta neredeydi?`, cevap: eski, tur: 'bellek' },
          ]

      /**
       * Seçenekler KÂĞIDA DA yazılıyor.
       *
       * Palet ekranın bir kolaylığı; yazdırma oluşturucusu onu
       * basmıyor. Çeldirici kaplar hikâyede geçmediği için kâğıttaki
       * çocuk onları hiç düşünmüyordu: aynı sayfa iki kanalda iki
       * ayrı zorluk veriyordu.
       */
      const secenekler = rng.shuffle([eski, yeni, ...celdirici])
      render.push({ t: 'text', value: `Yerler: ${secenekler.join(', ')}`, size: 'sm' })
      render.push({
        t: 'rows',
        items: sorular.map((s, i) => ({ label: s.soru, blank: true, id: `h${h}_${i}` })),
        columns: 1,
        palette: secenekler.map((k) => ({ glyph: k })),
      })

      sorular.forEach((s, i) => {
        cozumler.push({ id: `h${h}_${i}`, beklenen: s.cevap, tur: s.tur })
      })
    }

    return {
      code: 'false_belief',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Hikâyeyi oku ve soruları cevapla. Cevaplar aşağıdaki yerlerden biri.',
      spokenPrompt:
        'Sana kısa bir hikâye okuyacağım. Sonra kimin ne düşündüğünü soracağım; dikkatle dinle.',
      render,
      solution: cozumler,
      grading: 'auto',
      estimatedSeconds: hikayeSayisi * (60 + cozumler.length * 15),
      itemCount: cozumler.length,
    }
  },
  /**
   * Kontrol soruları AYRI işaretleniyor.
   *
   * "Yanlış inanış sorusunu bilemedi" ile "hikâyeyi hatırlamıyor"
   * aynı şey değil ve aynı puanı vermeleri uygulayıcıyı yanlış
   * yönlendiriyor. Madde detayında her sorunun türü yazıyor.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { id: string; beklenen: string; tur: string }[]
    const verilen = Array.isArray(answer) ? answer : []
    const ETIKET: Record<string, string> = {
      inanis: 'yanlış inanış',
      ikinci: 'ikinci derece inanış',
      gerceklik: 'gerçeklik kontrolü',
      bellek: 'bellek kontrolü',
    }
    const items: ItemResult[] = sol.map((s, i) => {
      const ham = verilen[i]
      const v = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      return {
        id: s.id,
        beklenen: `${s.beklenen} (${ETIKET[s.tur]})`,
        verilen: v,
        dogru: v !== null && foldTr(v) === foldTr(s.beklenen),
      }
    })
    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: items.length,
      accuracy: items.length ? dogru / items.length : 0,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { beklenen: string; tur: string }[]
    const ETIKET: Record<string, string> = {
      inanis: 'inanış',
      ikinci: '2. derece',
      gerceklik: 'gerçeklik',
      bellek: 'bellek',
    }
    return sol.map((s, i) => `${i + 1}. ${s.beklenen} [${ETIKET[s.tur]}]`).join('   ')
  },
}

/* ================================================================== *
 * 37. Sosyal alt metin
 *
 * Biri nazik bir şey söylüyor ama bedeni başka bir şey söylüyor.
 * İki soru: aslında ne hissetti, ve karşısındaki ne hissettiğini
 * SANDI.
 *
 * İkinci soru şablonun asıl fikri: gösteren kişi yalnızca SÖZÜ duydu,
 * ipucunu görmedi. Birinci soruyu bilip ikinciyi bilememek sık
 * rastlanan ve anlamlı bir örüntü — çocuk alt metni okuyor ama
 * ötekinin bilgisinin sınırını kuramıyor.
 *
 * İPUCU TEK BİR DUYGUYU GÖSTERMEK ZORUNDA. "Arkasını dönüp ofladı"
 * hem "sıkıldı" hem "kızdı" olarak okunabiliyor; ikisi birden
 * seçeneklerde olursa sayfanın iki doğru cevabı olur. Her ipucunun
 * yanında, seçeneklere GİREMEYECEK yakın duygular yazıyor.
 * ================================================================== */

const IPUCLARI: { ipucu: string; gercek: string; yasak: string[] }[] = [
  {
    ipucu: 'hemen arkasını dönüp ofladı',
    gercek: 'sıkıldı',
    yasak: ['kızdı', 'bıktı', 'öfkelendi'],
  },
  {
    ipucu: 'kaşlarını çattı ve başka yöne baktı',
    gercek: 'hoşlanmadı',
    yasak: ['kızdı', 'beğenmedi', 'sıkıldı'],
  },
  {
    ipucu: 'kendi eski olanına bakıp derin bir iç çekti',
    gercek: 'kıskandı',
    yasak: ['üzüldü', 'özendi', 'imrendi'],
  },
  {
    ipucu: 'eline alıp hemen geri verdi ve başka konu açtı',
    gercek: 'ilgilenmedi',
    yasak: ['sıkıldı', 'hoşlanmadı', 'önemsemedi'],
  },
  {
    ipucu: 'gözleri büyüdü ve bir an hiç konuşamadı',
    gercek: 'şaşırdı',
    yasak: ['korktu', 'heyecanlandı', 'donakaldı'],
  },
  /**
   * İpucu sözü DOĞRULAYAN iki kurgu.
   *
   * Hepsi "söyledi ama aslında başka" olsaydı çocuk sahneyi hiç
   * okumadan "söylenenin dışındakini seç" diye bir kısayol kurardı ve
   * biz alt metin okumayı ölçtüğümüzü sanırdık. Beden dili sözü
   * doğruladığında doğru cevap söylenen duygunun kendisi — ve bu da
   * bir alt metin okumasıdır: her uyumsuzluk yalan değil, her uyum
   * da değil.
   */
  {
    ipucu: 'gözleri parlayarak eline alıp evirip çevirdi',
    gercek: 'beğendi',
    /*
     * Doğrulayan iki ipucunun yasak listesi AYNI.
     *
     * Ayrı yazılınca bir kurguda yasak olan duygu ötekinde çeldirici
     * olarak çıkıyordu: cevabı "beğendi" olan bir soruda "merak etti"
     * seçeneği de savunulabilir hale geliyordu.
     */
    yasak: ['sevindi', 'heyecanlandı', 'merak etti'],
  },
  {
    ipucu: 'yüzü aydınlanarak nereden aldığını sordu',
    gercek: 'beğendi',
    yasak: ['sevindi', 'heyecanlandı', 'merak etti'],
  },
]

/**
 * Çeldirici duygu havuzu — ipuçlarından BAĞIMSIZ.
 *
 * İlk hali çeldiricileri öteki ipuçlarının gerçek duygularından
 * seçiyordu ve ipucu sayısı azaldıkça seçenek sayısı düşüyordu.
 */
const DUYGULAR = [
  'sıkıldı', 'hoşlanmadı', 'kıskandı', 'ilgilenmedi', 'şaşırdı',
  'üzüldü', 'utandı', 'sevindi', 'gururlandı', 'merak etti',
]

/** Söylenen şey hep olumlu: nazik cümlenin kendisi bir maske. */
const OVGULER = ['çok güzel olmuş', 'harika', 'çok beğendim', 'ne kadar güzel']
const SOYLENEN = 'beğendi'

export const socialCue: ExerciseTemplate = {
  code: 'social_cue',
  title: 'Aslında ne hissetti?',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç sahne */
      sahne: l >= 5 ? 2 : 1,
      /** Seçenek sayısı */
      secenek: scale(l, 3, 5),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const sahneSayisi = p['sahne'] as number
    const secenekSayisi = p['secenek'] as number

    const render: RenderNode[] = []
    const cozumler: { id: string; beklenen: string; tur: string }[] = []

    // Aynı sayfada aynı ipucu iki kez görünmesin
    const kurgular = rng.shuffle(IPUCLARI)
    for (let h = 0; h < sahneSayisi; h++) {
      const adlar = rng.shuffle(ADLAR)
      const [a, b] = [adlar[0]!, adlar[1]!]
      /* Gösterilebilir nesneler: "yeni aldığı anahtarı gösterdi" tuhaf */
      const nesne = rng.pick(NESNELER.filter((n) => n !== 'anahtar'))
      const kurgu = kurgular[h % kurgular.length]!
      const ovgu = rng.pick(OVGULER)

      /**
       * Bağlaç ipucuya göre: çelişiyorsa "ama", doğruluyorsa ";".
       *
       * "dedi ama gözleri parladı" cümlesi beden dilinin sözü
       * doğruladığı kurguda yanlış okunuyor — bağlacın kendisi bir
       * ipucu veriyor ve çocuğu sahneyi okumadan "uyumsuzluk var"
       * demeye itiyordu. "ve" de olmadı: doğrulayan ipuçlarının
       * içinde zaten "ve" geçiyor ve cümle "dedi ve ... ve ..."
       * oluyordu.
       */
      const metin =
        `${a}, yeni aldığı ${belirtme(nesne)} ${yonelme(b, true)} gösterdi. ` +
        (kurgu.gercek === SOYLENEN
          ? `${b} "${ovgu}" dedi; ${kurgu.ipucu}.`
          : `${b} "${ovgu}" dedi ama ${kurgu.ipucu}.`)

      /**
       * Çeldiriciler: ipucunun yakınındaki duygular DIŞARIDA.
       *
       * Yakın bir duygu seçeneklere girerse sayfanın iki savunulabilir
       * cevabı olur ve çocuğun verdiği doğru cevap yanlış sayılır.
       */
      const havuz = DUYGULAR.filter(
        (d) => d !== kurgu.gercek && d !== SOYLENEN && !kurgu.yasak.includes(d),
      )
      /*
       * Beden dili sözü doğruluyorsa gerçek duygu ile söylenen duygu
       * aynı; seçeneklerde iki kez görünmesin.
       */
      const temel = kurgu.gercek === SOYLENEN ? [SOYLENEN] : [kurgu.gercek, SOYLENEN]
      /*
       * Çeldirici sayısı TEMELDEN çıkıyor, sabit ikiden değil.
       * Sabit yazılınca doğrulayan ipuçlarında toplam seçenek sayısı
       * ikiye düşüyordu: iki seçenekli bir soru, yazı tura.
       */
      const celdirici = rng.shuffle(havuz).slice(0, Math.max(1, secenekSayisi - temel.length))
      const secenekler = rng.shuffle([...temel, ...celdirici])

      render.push({
        t: 'text',
        value: `${h + 1}. ${metin}`,
        size: 'md',
      })
      /*
       * Duygu seçenekleri kâğıtta da görünmek zorunda: ekranda palet
       * var, kâğıtta hiçbir şey yoktu ve çocuk hangi kelimeler
       * arasından seçeceğini bilmiyordu.
       */
      render.push({ t: 'text', value: `Duygular: ${secenekler.join(', ')}`, size: 'sm' })
      render.push({
        t: 'rows',
        items: [
          { label: `${b} aslında ne hissetti?`, blank: true, id: `s${h}_0` },
          { label: `${a}, ${tamlayan(b, true)} ne hissettiğini sandı?`, blank: true, id: `s${h}_1` },
        ],
        columns: 1,
        palette: secenekler.map((d) => ({ glyph: d })),
      })

      cozumler.push(
        { id: `s${h}_0`, beklenen: kurgu.gercek, tur: 'altmetin' },
        { id: `s${h}_1`, beklenen: SOYLENEN, tur: 'otekinin_bilgisi' },
      )
    }

    return {
      code: 'social_cue',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Sahneyi oku. Söylenen sözle beden dili aynı şeyi söylemiyor olabilir. ' +
        'İki soruyu da aşağıdaki duygulardan seçerek cevapla.',
      spokenPrompt:
        'Bir sahne okuyacağım. Kişinin söylediğiyle yaptığı aynı şeyi anlatmıyor olabilir.',
      render,
      solution: cozumler,
      grading: 'auto',
      estimatedSeconds: sahneSayisi * 70,
      itemCount: cozumler.length,
    }
  },
  /**
   * İki soru ayrı ayrı okunuyor.
   *
   * Birinciyi bilip ikinciyi bilememek anlamlı bir örüntü: çocuk alt
   * metni okuyor ama ötekinin bilgisinin sınırını kuramıyor. Tek bir
   * yüzde bunu göstermiyor.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { id: string; beklenen: string; tur: string }[]
    const verilen = Array.isArray(answer) ? answer : []
    const ETIKET: Record<string, string> = {
      altmetin: 'alt metin',
      otekinin_bilgisi: 'ötekinin bildiği',
    }
    const items: ItemResult[] = sol.map((s, i) => {
      const ham = verilen[i]
      const v = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      return {
        id: s.id,
        beklenen: `${s.beklenen} (${ETIKET[s.tur]})`,
        verilen: v,
        dogru: v !== null && foldTr(v) === foldTr(s.beklenen),
      }
    })
    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: items.length,
      accuracy: items.length ? dogru / items.length : 0,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { beklenen: string; tur: string }[]
    return sol
      .map((s, i) => `${i + 1}. ${s.beklenen}${s.tur === 'otekinin_bilgisi' ? ' (yalnızca sözü duydu)' : ''}`)
      .join('   ')
  },
}

/* ================================================================== *
 * 42. Göz çevresinden ifade okuma
 *
 * NE DEĞİL: bilinen fotoğraflı göz testi değil ve onun adını
 * taşımıyor. O testin maddeleri telifli, fotoğrafları bizde yok ve
 * gerçek bir yüzü okumak çizilmiş bir kaşı okumakla aynı beceri değil.
 * `render/gozler.ts`'in başındaki not bunu uzun uzun yazıyor.
 *
 * DUYGU KÜMESİ KAPALI VE KABA. Yedi tane: çizilmiş gözlerin
 * taşıyabildiği kadarı. "Kıskandı", "utandı", "mahcup oldu" burada yok
 * — fotoğrafta bile tartışmalı, çizimde ise biz uydurmuş oluruz.
 *
 * GEOMETRİ RASTGELE DEĞİL. Her ifade elle yazılmış bir tarif. Kaş
 * açısını rastgele örnekleyip "bu kızgın" demek, sayfanın doğru cevabı
 * olmadığı anlamına gelirdi. Rastgelelik yalnızca HANGİ ifadenin hedef
 * olduğunu ve hangi çeldiricilerin çıktığını seçiyor.
 *
 * KARIŞAN ÇİFTLER AYNI SAYFAYA GİRMİYOR. Fotoğraflı testin zorluğu
 * tam olarak ince ayrımdır ama onun ince ayrımları insan yargısına
 * karşı doğrulanmış; bizimki doğrulanmadı. Doğrulanmamış bir ayrımı
 * sayfaya koymak, çocuğun savunulabilir cevabını yanlış saymak demek.
 * O yüzden `KARISIR` çiftleri hiç birlikte görünmüyor ve zorluk başka
 * eksenlerden geliyor: seçenek sayısı, sahne sayısı, ve hedefin
 * kendisinin ne kadar ince olduğu.
 *
 * İKİ YÖN. Düşük düzeyde çizim verilip kelime soruluyor; 6. düzeyden
 * sonra ayrıca kelime verilip üç çizim arasından seçtiriliyor. İkinci
 * yön karşılaştırma istiyor: tek bir çizime bakıp karar vermek ile üç
 * çizimi birbirine göre tartmak aynı iş değil.
 * ================================================================== */

/**
 * İfade tarifleri.
 *
 * `zor` olanlar yalnızca 4. düzeyden sonra HEDEF olabiliyor; çeldirici
 * olarak her düzeyde çıkabilirler — bir çeldiricinin ince olması
 * sayfayı zorlaştırmıyor, hedefin ince olması zorlaştırıyor.
 */
export const IFADELER: { ad: string; zor: boolean; cizim: GozCizimi }[] = [
  {
    ad: 'kızgın',
    zor: false,
    // Kaşın iç ucu aşağıda ve buruna doğru kaymış: çatık kaş
    cizim: { kasIc: 17, kasDis: 28, kasKavis: 0, kasIceri: 6, ustAcik: 13, altAcik: 7, bakisX: 0, bakisY: 0, disKose: 0 },
  },
  {
    ad: 'şaşırmış',
    zor: false,
    // İki kaş birden yukarı, kapak çok açık
    cizim: { kasIc: 37, kasDis: 36, kasKavis: 6, kasIceri: 0, ustAcik: 24, altAcik: 12, bakisX: 0, bakisY: 0, disKose: 0 },
  },
  {
    ad: 'üzgün',
    zor: false,
    // İÇ uç yukarı (çatık kaşın tersi), dış köşe düşmüş, bakış aşağı
    cizim: { kasIc: 35, kasDis: 20, kasKavis: 2, kasIceri: 0, ustAcik: 13, altAcik: 9, bakisX: 0, bakisY: 0.45, disKose: 7 },
  },
  {
    ad: 'mutlu',
    zor: false,
    /*
     * Gülen gözün ayırt edici yeri ALT kapak: yukarı kalkıyor
     * (`altAcik` küçük). Kaş değişmiyor. Üst kapağı kısmak yetmezdi —
     * o uykulu gözle aynı şey olurdu.
     */
    cizim: { kasIc: 26, kasDis: 26, kasKavis: 5, kasIceri: 0, ustAcik: 16, altAcik: -5, bakisX: 0, bakisY: 0, disKose: 0 },
  },
  {
    ad: 'uykulu',
    zor: true,
    // Üst kapak çok düşük; iris büyük ölçüde kapağın altında kalıyor
    cizim: { kasIc: 21, kasDis: 21, kasKavis: 1, kasIceri: 0, ustAcik: 6, altAcik: 6, bakisX: 0, bakisY: 0.15, disKose: 3 },
  },
  {
    ad: 'kuşkulu',
    zor: true,
    // TEK kaş kalkık: simetrik parametrelerle anlatılamayan tek ifade
    cizim: {
      kasIc: 22, kasDis: 24, kasKavis: 1, kasIceri: 0, ustAcik: 13, altAcik: 7, bakisX: 0.75, bakisY: 0, disKose: 0,
      sagKas: { kasIc: 36, kasDis: 30, kasKavis: 5 },
    },
  },
  {
    ad: 'düşünüyor',
    zor: true,
    // Bakış yukarı-yana; kaş neredeyse değişmiyor
    cizim: { kasIc: 23, kasDis: 25, kasKavis: 2, kasIceri: 0, ustAcik: 18, altAcik: 10, bakisX: 0.7, bakisY: -0.7, disKose: 0 },
  },
]

/**
 * Şematik çizimde birbirinden ayrılamayan çiftler.
 *
 * Aynı sayfada asla ikisi birden görünmüyor. Liste elle yazılıyor ama
 * sınama tek yönlü bir denetim koyuyor: imzası AYNI olan iki tarif
 * burada yazılı OLMAK ZORUNDA. Yani geometrinin ayırt edemediği bir
 * çifti unutmak derlenip geçmiyor. Tersi serbest — geometrisi farklı
 * olduğu hâlde insan gözüne yakın gelen bir çifti de buraya
 * yazabiliyoruz, ve iki çift tam olarak öyle:
 *
 * - kuşkulu / düşünüyor: ikisinde de bakış yana kaçıyor ve kaş az
 *   oynuyor. Fark tek kaşın kalkıklığı; çocuğun onu kaçırması
 *   savunulabilir.
 * - uykulu / üzgün: ikisinde de kapak düşük ve bakış aşağıda. Fark
 *   kaşın iç ucunda.
 */
export const KARISIR: [string, string][] = [
  ['kuşkulu', 'düşünüyor'],
  ['uykulu', 'üzgün'],
]

export function karisirMi(a: string, b: string): boolean {
  return KARISIR.some(([x, y]) => (x === a && y === b) || (x === b && y === a))
}

/** Hedefle aynı sayfaya girebilen ifadeler */
function uygunCeldiriciler(hedef: string): string[] {
  return IFADELER.map((i) => i.ad).filter((ad) => ad !== hedef && !karisirMi(ad, hedef))
}

function tarif(ad: string): { ad: string; zor: boolean; cizim: GozCizimi } {
  const t = IFADELER.find((i) => i.ad === ad)
  if (!t) throw new Error(`Bilinmeyen ifade: ${ad}`)
  return t
}

const HARFLER = ['A', 'B', 'C', 'D']

export const eyeEmotion: ExerciseTemplate = {
  code: 'eye_emotion',
  title: 'Gözler ne anlatıyor?',
  /**
   * Alanlar: seçici dikkat + esneklik.
   *
   * Sözel işleyen bellek DEĞİL — öteki iki sosyal şablonun aksine
   * burada tutulacak bir hikâye yok. Çocuğun yaptığı iş çizimdeki
   * belirli ayrıntıya bakmak (seçici dikkat) ve tek bir kurala
   * indirgenemeyen bir eşlemeyi uygulamak (esneklik).
   */
  domains: ['selective_attention', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Çizim verilip kelime sorulan sahne sayısı */
      sahne: scale(l, 1, 2),
      /** Kelime seçeneği sayısı */
      secenek: scale(l, 3, 4),
      /** İnce ifadeler de hedef olabilir mi */
      zorHedef: l >= 4,
      /** Kelime verilip çizim seçtirilen soru var mı */
      tersYon: l >= 6,
      /** Ters yönde kaç çizim gösteriliyor */
      tersSecenek: scale(l, 3, 4),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const sahneSayisi = p['sahne'] as number
    const secenekSayisi = p['secenek'] as number
    const zorHedef = p['zorHedef'] as boolean
    const tersYon = p['tersYon'] as boolean
    const tersSecenek = p['tersSecenek'] as number

    const render: RenderNode[] = []
    const cozumler: { id: string; beklenen: string; tur: string; ifade: string }[] = []

    /*
     * Hedef havuzu düzeye göre: ince ifadeler 4. düzeyden önce hedef
     * olmuyor. Havuz karıştırılıp sırayla tüketiliyor — aynı sayfada
     * aynı hedef iki kez çıkmasın.
     */
    const hedefHavuzu = rng.shuffle(
      IFADELER.filter((i) => zorHedef || !i.zor).map((i) => i.ad),
    )

    /* ---- Yön 1: çizim verildi, kelime soruluyor ---- */
    for (let h = 0; h < sahneSayisi; h++) {
      const hedef = hedefHavuzu[h % hedefHavuzu.length]!
      const celdirici = rng
        .shuffle(uygunCeldiriciler(hedef))
        .slice(0, Math.max(1, secenekSayisi - 1))
      const secenekler = rng.shuffle([hedef, ...celdirici])

      render.push({
        t: 'eyes',
        cols: 1,
        items: [{ cizim: tarif(hedef).cizim, id: `g${h}` }],
      })
      /*
       * Seçenekler KÂĞIDA DA yazılıyor: ekranda palet var, kâğıtta
       * hiçbir şey olmasaydı çocuk hangi kelimeler arasından seçeceğini
       * bilmiyordu. `social_cue`'da aynı hata bir kez yapıldı.
       */
      render.push({ t: 'text', value: `Seçenekler: ${secenekler.join(', ')}`, size: 'sm' })
      render.push({
        t: 'rows',
        items: [{ label: `${h + 1}. Bu gözler ne anlatıyor?`, blank: true, id: `y${h}` }],
        columns: 1,
        palette: secenekler.map((d) => ({ glyph: d })),
      })
      cozumler.push({ id: `y${h}`, beklenen: hedef, tur: 'okuma', ifade: hedef })
    }

    /* ---- Yön 2: kelime verildi, çizim seçtiriliyor ---- */
    if (tersYon) {
      /*
       * Hedef, yön 1'de kullanılmayanlardan: aynı sayfada aynı çizime
       * iki kez cevap vermek, ikinci soruyu birincinin kopyası yapıyor.
       */
      const kullanilan = cozumler.map((c) => c.ifade)
      const kalan = hedefHavuzu.filter((a) => !kullanilan.includes(a))
      const hedef = (kalan[0] ?? hedefHavuzu[0])!
      /*
       * Çeldiriciler birbirleriyle de karışmamalı. Yön 1'de bu sorun
       * yok (çeldiriciler yalnızca kelime), burada her çeldirici bir
       * ÇİZİM ve birbirine karışan iki çizim sayfada iki savunulabilir
       * cevap demek.
       *
       * Önce kesip sonra süzmek YETMİYOR: kesilen üçlünün içinden bir
       * çift düşünce elde kalan iki çizim oluyor ve soru yazı turaya
       * dönüyordu. Havuzun TAMAMI geziliyor, gerekli sayıya ulaşınca
       * duruyor.
       */
      const temiz: string[] = []
      for (const c of rng.shuffle(uygunCeldiriciler(hedef))) {
        if (temiz.length >= Math.max(1, tersSecenek - 1)) break
        if (temiz.every((t) => !karisirMi(t, c))) temiz.push(c)
      }
      const dizilim = rng.shuffle([hedef, ...temiz])
      const dogruHarf = HARFLER[dizilim.indexOf(hedef)]!

      render.push({
        t: 'eyes',
        cols: dizilim.length,
        items: dizilim.map((ad, i) => ({
          cizim: tarif(ad).cizim,
          etiket: HARFLER[i]!,
          id: `t${i}`,
        })),
      })
      render.push({
        t: 'rows',
        items: [
          {
            label: `${sahneSayisi + 1}. Hangi gözler "${hedef}" anlatıyor?`,
            blank: true,
            id: 'ters',
          },
        ],
        columns: 1,
        palette: dizilim.map((_, i) => ({ glyph: HARFLER[i]! })),
      })
      cozumler.push({ id: 'ters', beklenen: dogruHarf, tur: 'eslestirme', ifade: hedef })
    }

    return {
      code: 'eye_emotion',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Yalnızca gözlere bak: kaşların eğimine, göz kapaklarının açıklığına ve ' +
        'bakışın yönüne. Cevabı aşağıdaki seçeneklerden seç.',
      spokenPrompt:
        'Sana çizilmiş gözler göstereceğim. Yüzün geri kalanı yok; yalnızca gözlerden ' +
        'ne anlattığını bulacaksın.',
      render,
      solution: cozumler,
      grading: 'auto',
      estimatedSeconds: cozumler.length * 35,
      itemCount: cozumler.length,
    }
  },
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { id: string; beklenen: string; tur: string; ifade: string }[]
    const verilen = Array.isArray(answer) ? answer : []
    const items: ItemResult[] = sol.map((s, i) => {
      const ham = verilen[i]
      const v = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
      return {
        id: s.id,
        /*
         * Eşleştirme sorusunda beklenen HARF; harf tek başına
         * uygulayıcıya bir şey söylemiyor, ifade adı da yazılıyor.
         */
        beklenen: s.tur === 'eslestirme' ? `${s.beklenen} (${s.ifade})` : s.beklenen,
        verilen: v,
        dogru: v !== null && foldTr(v) === foldTr(s.beklenen),
      }
    })
    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: items.length,
      accuracy: items.length ? dogru / items.length : 0,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { beklenen: string; tur: string; ifade: string }[]
    return sol
      .map((s, i) =>
        s.tur === 'eslestirme'
          ? `${i + 1}. ${s.beklenen} = ${s.ifade}`
          : `${i + 1}. ${s.beklenen}`,
      )
      .join('   ')
  },
}
